import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';

// Page components — lazy-loaded for code splitting
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const CallbackPage = React.lazy(() => import('./pages/CallbackPage'));
const UploadLinkPage = React.lazy(() => import('./pages/UploadLinkPage'));
const DownloadLinkPage = React.lazy(() => import('./pages/DownloadLinkPage'));
const UploadLinksPage = React.lazy(() => import('./pages/UploadLinksPage'));
const MediaLibraryPage = React.lazy(() => import('./pages/MediaLibraryPage'));
const FolderViewPage = React.lazy(() => import('./pages/FolderViewPage'));
const FavouritesPage = React.lazy(() => import('./pages/FavouritesPage'));
const CollectionsPage = React.lazy(() => import('./pages/CollectionsPage'));
const CollectionDetailPage = React.lazy(() => import('./pages/CollectionDetailPage'));
const UploadPage = React.lazy(() => import('./pages/UploadPage'));
const MediaDetailPage = React.lazy(() => import('./pages/MediaDetailPage'));
const SearchPage = React.lazy(() => import('./pages/SearchPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const FolderManagementPage = React.lazy(() => import('./pages/admin/FolderManagementPage'));
const FaceCollectionPage = React.lazy(() => import('./pages/admin/FaceCollectionPage'));
const SharePage = React.lazy(() => import('./pages/SharePage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <React.Suspense
        fallback={
          <div className="flex h-screen items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          </div>
        }
      >
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          {/* OAuth callback — waits for Amplify to exchange the code */}
          <Route path="/callback" element={<CallbackPage />} />
          {/* Public upload link page (no auth required) */}
          <Route path="/upload-link/:token" element={<UploadLinkPage />} />
          {/* Public download link gallery page (no auth required) */}
          <Route path="/download-link/:token" element={<DownloadLinkPage />} />

          {/* Protected routes — wrapped in AppLayout for navigation */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Navigate to="/media" replace />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/media"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <MediaLibraryPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/folders"
            element={
              <ProtectedRoute internalOnly>
                <AppLayout>
                  <FolderViewPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {/* Folder management (create/rename/move; delete is admin-only and
              enforced by the backend). Available to event-managers and admins. */}
          <Route
            path="/folders-manage"
            element={
              <ProtectedRoute eventManagerOrAdmin>
                <AppLayout>
                  <FolderManagementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {/* Face collection management — event-managers and admins have full
              parity (manage persons/faces). */}
          <Route
            path="/faces"
            element={
              <ProtectedRoute eventManagerOrAdmin>
                <AppLayout>
                  <FaceCollectionPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/favourites"
            element={
              <ProtectedRoute internalOnly>
                <AppLayout>
                  <FavouritesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/collections"
            element={
              <ProtectedRoute internalOnly>
                <AppLayout>
                  <CollectionsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/collections/:id"
            element={
              <ProtectedRoute internalOnly>
                <AppLayout>
                  <CollectionDetailPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/media/:id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <MediaDetailPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/upload"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <UploadPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <SearchPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/share"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <SharePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/upload-links"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <UploadLinksPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute adminOnly>
                <AppLayout>
                  <AdminPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
};

export default App;
